import React, { useState } from 'react';
import './CollabEnhancements.scss';
import CTAButton from '../UI/CTA/CTAButton/CTAButton';
import { Tween, Timeline } from 'react-gsap';

/**
 * Renders the collab enhancements module
 * @param {boolean} isEnabled
 * @param {string} title - module title
 * @param {array} products
 * @returns {JSX.Element}
 * @constructor
 */
const CollabEnhancements = ({ isEnabled, title, products }) => {
  const [cards, setCards] = useState(
    products.map((product, i) => {
      return {
        ...product,
        isExpanded: i === 0,
      };
    }),
  );

  const [activeIndex, setActiveIndex] = useState(0);

  /**
   * Renders the animation template
   * @param {object} card
   * @returns {JSX.Element}
   */
  const animationTemplate = (card) => (
    <div className="c-collab-enhancements__product-card-animation" style={{ overflow: 'hidden' }}>
      <div className="c-collab-enhancements__product-image">
        <img src={card?.productImage?.url} alt={card?.productImage?.alt} />
      </div>
      <div className="c-collab-enhancements__product-description body-2">
        <p>{card?.shortDescription}</p>
      </div>
      <div className="c-collab-enhancements__product-cta">
        <CTAButton
          type={'primary'}
          link={card?.ctaLink}
          text={`Shop ${card?.ctaText}`}
          target={'_self'}
          additionalClasses={'cta'}
        />
      </div>
    </div>
  );

  /**
   * Tween responsible for animating in
   * @param {object} card
   * @returns {JSX.Element}
   * @constructor
   */
  const AnimateIn = ({ card }) => (
    <Timeline target={animationTemplate(card)}>
      <Tween
        from={{ height: 0 }}
        to={{ height: 'auto' }}
        duration={'.8'}
        position={'0'}
        ease={'power2.out'}
      />
      <Tween
        from={{ opacity: 0 }}
        to={{ opacity: 1 }}
        duration={'.2'}
        position={'.8'}
        ease={'power2.inOut'}
      />
    </Timeline>
  );

  /**
   * Tween Responsible for animating out
   * @param {object} card
   * @returns {JSX.Element}
   * @constructor
   */
  const AnimateOut = ({ card }) => (
    <Timeline target={animationTemplate(card)}>
      <Tween
        from={{ height: 'auto' }}
        to={{ height: 0 }}
        duration={'.8'}
        position={'0'}
        ease={'power2.out'}
      />
      <Tween
        from={{ opacity: 1 }}
        to={{ opacity: 0 }}
        duration={'.2'}
        position={'0'}
        ease={'power2.inOut'}
      />
    </Timeline>
  );

  /**
   * Renders the product cards
   * @param {array} cards
   * @returns {*}
   */
  const renderProductCards = (cards) =>
    cards.map((card, i) => (
      <div
        key={card?.ctaLink}
        className={`c-collab-enhancements__product-card ${
          cards[i]?.isExpanded ? 'is-expanded' : 'is-closed'
        }`}
        onClick={() => toggleExpanded(i)}
      >
        <div className="c-collab-enhancements__product-card-inner">
          <div className="c-collab-enhancements__product-card-header">
            <div className="c-collab-enhancements__product-title h3-roobert">
              <h3>{card?.title}</h3>
            </div>
            <div className="c-collab-enhancements__product-price eyebrow-1">
              <span>{card?.price}</span>
            </div>
          </div>
          {products.length > 1 ? (
            <Timeline
              target={card?.isExpanded ? <AnimateIn card={card} /> : <AnimateOut card={card} />}
            />
          ) : (
            animationTemplate(card)
          )}
        </div>
      </div>
    ));

  /**
   * Toggle the expanded card
   * @param {number} selectedIndex - the index of clicked card
   */
  const toggleExpanded = (selectedIndex) => {
    const updated = [...cards];
    setCards(
      updated.map((card, i) => {
        return {
          ...card,
          isExpanded: i === selectedIndex,
        };
      }),
    );
    setActiveIndex(selectedIndex);
  };

  return isEnabled ? (
    <div className="c-collab-enhancements background-color-gray">
      <div className="c-collab-enhancements__inner">
        <div className="c-collab-enhancements__title h2-roobert">
          <h2>{title}</h2>
        </div>
        <div className="c-collab-enhancements__content">
          <div className="c-collab-enhancements__main-image">
            {/* There is a max of two cards */}
            <img
              className={activeIndex === 0 ? 'is-visible' : 'is-hidden'}
              src={cards[0]?.mainImage?.url}
              alt={cards[0]?.mainImage?.alt}
            />
            {products.length > 1 && (
              <img
                className={activeIndex === 1 ? 'is-visible' : 'is-hidden'}
                src={cards[1]?.mainImage?.url}
                alt={cards[1]?.mainImage?.alt}
              />
            )}
          </div>
          <div className="c-collab-enhancements__cards-container">{renderProductCards(cards)}</div>
        </div>
      </div>
    </div>
  ) : null;
};

export default CollabEnhancements;
